import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

/**
 * Lets a page inject its header into the app layout's top bar, so detail pages
 * share one header design instead of stacking a second card header under it.
 *
 * Two slots: `title` is centered in the bar; `actions` (quick-edit dropdowns,
 * buttons) are right-aligned next to the global controls (notification bell).
 */
interface PageHeaderContextValue {
  title: ReactNode;
  actions: ReactNode;
  setHeader: (title: ReactNode, actions: ReactNode) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue>({
  title: null,
  actions: null,
  setHeader: () => {},
});

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<ReactNode>(null);
  const [actions, setActions] = useState<ReactNode>(null);
  const setHeader = useCallback((t: ReactNode, a: ReactNode) => {
    setTitle(t);
    setActions(a);
  }, []);
  return (
    <PageHeaderContext.Provider value={{ title, actions, setHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

/** Read side — used by AppHeader. */
export function usePageHeaderTitle(): ReactNode {
  return useContext(PageHeaderContext).title;
}
export function usePageHeaderActions(): ReactNode {
  return useContext(PageHeaderContext).actions;
}

/**
 * Write side — pages re-set their header on every dep change and clear it
 * on unmount (so navigating away never leaves stale controls in the bar).
 */
export function usePageHeader(title: ReactNode, actions: ReactNode, deps: unknown[]) {
  const { setHeader } = useContext(PageHeaderContext);
  useEffect(() => {
    setHeader(title, actions);
    return () => setHeader(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
